<template>
  <div id="clientSearchAutocomplete">
    <v-autocomplete v-model="model"
                    :items="clients"
                    :loading="isLoading"
                    :search-input.sync="search"
                    outlined
                    prepend-inner-icon="icon-search"
                    :placeholder="placeholderText"
                    :no-data-text="noResultsText"
                    attach="#clientSearchAutocomplete"
                    color="black"
                    :hide-no-data="hideEmptyMessage"
                    return-object
                    item-text="uid"
                    :filter="itemFilter"
                    @input="onClientClick"
                    :menu-props="menuProps">
      <template v-slot:item="data">
        <ClientRow :client="data.item"/>
        <v-btn :color="secondaryColor" class="client-select-button">
          {{$t('common.words.select')}}
        </v-btn>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
  import {mapGetters} from "vuex";
  import {searchService} from "@/modules/common/services";
  import ClientRow from "../ClientRow/ClientRow";

  export default {
    name: "ClientAutocomplete",
    components: {ClientRow},
    props: {
      placeholderText: {
        type: String,
        required: false
      },
      noResultsText: {
        type: String,
        required: false
      },
      currentClient: {
        type: Object,
        required: false
      },
      direction: {
        type: String,
        required: false,
        default: 'ltr'
      }
    },
    data() {
      return {
        clients: [],
        isLoading: false,
        model: null,
        search: null,
        timeoutId: null
      }
    },
    computed: {
      ...mapGetters('brandModule', ['secondaryColor']),
      hideEmptyMessage() {
        return !this.search || this.search.length < 2 || this.isLoading;
      },
      menuProps() {
        return {
          maxHeight: 'calc(100% - 246px)',
          minWidth: '100%',
          nudgeRight: !this.clients.length && !this.isRtl ? 20 : 0,
          nudgeLeft: !this.clients.length && this.isRtl ? 20 : 0,
        };
      },
      isRtl() {
        return this.direction === 'rtl';
      }
    },
    watch: {
      search(val) {
        clearTimeout(this.timeoutId);

        if (!val || val.length < 2) {
          this.clients = [];
          this.isLoading = false;
          return;
        }

        this.isLoading = true;

        this.timeoutId = setTimeout(() => {
          this.searchQuery(val);
        }, 300);
      }
    },
    methods: {
      searchQuery(val) {
        searchService.search({query: val, entity: 'client'}).then(res => {
          if (this.isLoading) {
            this.clients = res.data && res.data.top_hits && res.data.top_hits.client || [];

            if (this.currentClient) {
              this.clients = this.clients.filter(client => client.uid !== this.currentClient.uid);
            }
          }
        }).finally(() => {
          this.isLoading = false;
        });
      },
      itemFilter(item) {
        return item;
      },
      onClientClick(client) {
        this.$emit('client-clicked', client);
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "@/assets/scss/abstract/_variables.scss";
  @import "@/assets/scss/abstract/_mixins.scss";

  #clientSearchAutocomplete {

    ::v-deep .v-input {
      font-size: 15px;

      &.v-text-field--outlined {

        fieldset {
          border-color: $grey-color-7;
          transition: .2s;
        }

        &.v-input--is-focused {

          fieldset {
            box-shadow: 0 0 0 2px $brand_secondary_color;
            border: solid 1px $brand_secondary_color;
            transition: .2s;
          }
        }
      }

      .v-input__slot {
        min-height: 52px;
      }

      .v-input__append-inner {
        display: none;
      }

      .v-input__prepend-inner {
        @include i-margin-mirror(0, 8px);
        margin-top: 15px;

        .v-icon {
          font-size: 26px;
          color: $grey-color-8;
        }
      }
    }

    ::v-deep .v-autocomplete__content {
      top: auto !important;
      box-shadow: none;
      margin-top: -10px;

      .v-list-item {

        &__content {
          text-transform: none;
        }

        .client-select-button {
          position: absolute;
          @include i-position-mirror(right, 30px);
          height: 30px;
          width: 75px;
          color: $white-color-1;
          font-size: 13px;
          font-weight: bold;
          border-radius: 4px;
          visibility: hidden;
          opacity: 0;
          transition: visibility 0s, opacity 0.2s linear;
        }

        &:hover {

          .client-select-button {
            visibility: visible;
            opacity: 1;
          }
        }
      }
    }
  }
</style>
